import apiClient from '../../shared/services/apiClient';

/**
 * SSL Service (Admin Version)
 *
 * Handles admin-level SSL operations with full CRUD capabilities.
 * Admins can manage SSL providers, products, and view all user certificates/orders.
 *
 * All routes use /api/admin/ssl/* prefix
 */
class SSLService {
  // ==========================================
  // Order Management
  // ==========================================

  /**
   * Get all SSL orders (admin view)
   * @param {object} params - Query parameters (user_id, status, etc.)
   */
  async getOrders(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/ssl/orders', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get single SSL order
   * @param {number} id - Order ID
   */
  async getOrder(id) {
    try {
      const response = await apiClient.get('/api/admin/ssl/orders/get', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update SSL order
   * @param {number} id - Order ID
   * @param {object} data - Updated order data
   */
  async updateOrder(id, data) {
    try {
      const response = await apiClient.put('/api/admin/ssl/orders', {
        id,
        ...data
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Cancel SSL order
   * @param {number} id - Order ID
   */
  async cancelOrder(id) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/cancel', {
        id
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Submit domain validation
   * @param {number} orderId - Order ID
   * @param {string} validationMethod - Validation method
   */
  async submitValidation(orderId, validationMethod) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/validate', {
        order_id: orderId,
        validation_method: validationMethod
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Download certificate
   * @param {number} orderId - Order ID
   */
  async downloadCertificate(orderId) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/download', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Provider Management (Admin Only)
  // ==========================================

  /**
   * Get SSL providers
   */
  async getProviders() {
    try {
      const response = await apiClient.get('/api/admin/ssl/providers');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get available SSL plugins
   */
  async getPlugins() {
    try {
      const response = await apiClient.get('/api/admin/ssl/plugins');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create SSL provider
   * @param {object} data - Provider data (name, api_endpoint, credentials, etc.)
   */
  async createProvider(data) {
    try {
      const response = await apiClient.post('/api/admin/ssl/providers', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update SSL provider
   * @param {number} id - Provider ID
   * @param {object} data - Updated provider data
   */
  async updateProvider(id, data) {
    try {
      const response = await apiClient.put('/api/admin/ssl/providers', {
        id,
        ...data
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete SSL provider
   * @param {number} id - Provider ID
   */
  async deleteProvider(id) {
    try {
      const response = await apiClient.delete('/api/admin/ssl/providers', {
        data: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Generate ACME EAB credentials from ZeroSSL API key
   * @param {string} apiKey - ZeroSSL API key
   */
  async generateEab(apiKey) {
    try {
      const response = await apiClient.post('/api/admin/ssl/providers/generate-eab', {
        api_key: apiKey
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Persist EAB credentials onto a provider
   * @param {number} providerId - Provider ID
   * @param {string} eabKid - EAB Key ID
   * @param {string} eabHmacKey - EAB HMAC Key
   */
  async configureEab(providerId, eabKid, eabHmacKey) {
    try {
      const response = await apiClient.post('/api/admin/ssl/providers/configure-eab', {
        provider_id: providerId,
        eab_kid: eabKid,
        eab_hmac_key: eabHmacKey
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Test provider connection
   * @param {number} id - Provider ID
   */
  async testProvider(id) {
    try {
      const response = await apiClient.post('/api/admin/ssl/providers/test', {
        id
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Product Management (Admin Only)
  // ==========================================

  /**
   * Get SSL products
   */
  async getProducts() {
    try {
      const response = await apiClient.get('/api/admin/ssl/products');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Create SSL product
   * @param {object} data - Product data (name, provider_id, type, pricing, etc.)
   */
  async createProduct(data) {
    try {
      const response = await apiClient.post('/api/admin/ssl/products', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update SSL product
   * @param {number} id - Product ID
   * @param {object} data - Updated product data
   */
  async updateProduct(id, data) {
    try {
      const response = await apiClient.put('/api/admin/ssl/products', {
        id,
        ...data
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete SSL product
   * @param {number} id - Product ID
   */
  async deleteProduct(id) {
    try {
      const response = await apiClient.delete('/api/admin/ssl/products', {
        data: { id }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Sync product catalog from GoGetSSL provider
   */
  async syncGoGetSSLProducts() {
    try {
      const response = await apiClient.post('/api/admin/ssl/products/sync-gogetssl', {});
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /** Provider price sources (SSL, domains …) with their last run */
  async getPriceSources() {
    try {
      const response = await apiClient.get('/api/admin/pricing/sources');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /** Run one price source now (cost refresh / markup / loss guard; read-only upstream) */
  async runPriceSource(key) {
    try {
      const response = await apiClient.post('/api/admin/pricing/sources/sync', { key });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /** Approve / reject a parked cost drop */
  async reviewProductPrice(id, action) {
    try {
      const response = await apiClient.post('/api/admin/ssl/products/price-review', { id, action });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /** SSL markup defaults + daily sync switch */
  async getPricingDefaults() {
    try {
      const response = await apiClient.get('/api/admin/ssl/pricing');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async savePricingDefaults(data) {
    try {
      const response = await apiClient.put('/api/admin/ssl/pricing', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Order Lifecycle Management (Admin Only)
  // ==========================================

  /**
   * Configure domain for pending order
   * @param {number} orderId - Order ID
   * @param {string} domain - Domain name
   * @param {array} sanDomains - Optional SAN domains
   */
  async configureOrder(orderId, domain, sanDomains = []) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/configure', {
        order_id: orderId,
        domain,
        san_domains: sanDomains
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Renew SSL order
   * @param {number} orderId - Order ID
   * @param {boolean} freeRenewal - Whether this is a free renewal (admin only)
   */
  async renewOrder(orderId, freeRenewal = false) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/renew', {
        order_id: orderId,
        free_renewal: freeRenewal
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Suspend SSL order
   * @param {number} orderId - Order ID
   * @param {string} reason - Suspension reason
   */
  async suspendOrder(orderId, reason = '') {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/suspend', {
        order_id: orderId,
        reason
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Resume SSL order
   * @param {number} orderId - Order ID
   */
  async resumeOrder(orderId) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/resume', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Revoke SSL certificate
   * @param {number} orderId - Order ID
   * @param {string} reason - Revocation reason
   */
  async revokeOrder(orderId, reason = '') {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/revoke', {
        order_id: orderId,
        reason
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Reissue SSL certificate
   * @param {number} orderId - Order ID
   */
  async reissueOrder(orderId) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/reissue', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get validation status
   * @param {number} orderId - Order ID
   */
  async getValidationStatus(orderId) {
    try {
      const response = await apiClient.get('/api/admin/ssl/orders/validation-status', {
        params: { order_id: orderId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Refresh order status from provider
   * @param {number} orderId - Order ID
   */
  async refreshOrderStatus(orderId) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/refresh-status', {
        order_id: orderId
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Change validation method
   * @param {number} orderId - Order ID
   * @param {string} method - New validation method (dns, http, email)
   */
  async changeValidationMethod(orderId, method) {
    try {
      const response = await apiClient.post('/api/admin/ssl/orders/change-validation', {
        order_id: orderId,
        validation_method: method
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Statistics & Reports (Admin Only)
  // ==========================================

  /**
   * Get SSL statistics
   * @param {object} params - Query parameters (date range, etc.)
   */
  async getStats(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/ssl/stats', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // ACME subscriptions (Sectigo CaaS)
  // ==========================================

  async getAcmeList(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/ssl/acme', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getAcme(orderId) {
    try {
      const response = await apiClient.get('/api/admin/ssl/acme/get', { params: { order_id: orderId } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async refreshAcme(orderId, itemId) {
    try {
      const response = await apiClient.post('/api/admin/ssl/acme/refresh', { order_id: orderId, item_id: itemId || undefined });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async cancelAcme(orderId, reason) {
    try {
      const response = await apiClient.post('/api/admin/ssl/acme/cancel', { order_id: orderId, reason });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new SSLService();
